import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState('0-0');

  const faqs = [
    {
      category: 'General',
      questions: [
        {
          q: 'What is Script Saint?',
          a: 'Script Saint is a marketplace for production-ready, well-documented code and complete websites. We offer prebuilt solutions in various tech stacks including Next.js, React, Flask, Django, and Node.js.'
        },
        {
          q: 'How do I get access to the code after purchase?',
          a: 'After successful payment, you will receive a GitHub repository invite link via email. The repository contains the complete source code, documentation, and deployment guides.'
        },
        {
          q: 'Can I use the purchased code for commercial projects?',
          a: 'Yes! All our products come with a commercial license. You can use them for your own projects or client work. Please check individual product licenses for specific terms.'
        },
        {
          q: 'Do you offer refunds?',
          a: 'Due to the nature of digital products, we generally don\'t offer refunds once you\'ve accessed the code. However, if you encounter any issues, please contact support and we\'ll do our best to help.'
        }
      ]
    },
    {
      category: 'Technical',
      questions: [
        {
          q: 'What technologies do you support?',
          a: 'We offer solutions in multiple tech stacks including React, Next.js, Node.js, Python (Flask, Django), and various databases (PostgreSQL, MongoDB, MySQL). Each product listing specifies its tech stack.'
        },
        {
          q: 'Is the code production-ready?',
          a: 'Yes! All our products are production-ready with proper error handling, security best practices, environment configuration, and performance optimizations. They also include CI/CD examples.'
        },
        {
          q: 'Do you provide documentation?',
          a: 'Every product includes comprehensive documentation covering setup, deployment, architecture, API references, and customization guides. You also get sample environment files and seed data.'
        },
        {
          q: 'Can I customize the code?',
          a: 'Absolutely! You get full access to the source code and can customize it according to your needs. The code is well-structured and documented to make customization easy.'
        }
      ]
    },
    {
      category: 'Deployment & Support',
      questions: [
        {
          q: 'Do you help with deployment?',
          a: 'Yes! Each product includes deployment guides. We also offer white-glove deployment services as a paid add-on where our team handles the entire deployment process for you.'
        },
        {
          q: 'What kind of support do you provide?',
          a: 'We provide email support for all purchases. Premium support packages with priority response times and video consultations are available as add-ons.'
        },
        {
          q: 'Do you offer updates?',
          a: 'Yes, we regularly update our products with bug fixes, security patches, and new features. All updates are free for lifetime license holders.'
        },
        {
          q: 'Can you build a custom solution for me?',
          a: 'Yes! We offer custom development services. You can either purchase a prebuilt product and hire us to customize it, or we can build a completely custom solution from scratch. Contact us for a quote.'
        }
      ]
    },
    {
      category: 'Pricing & Licensing',
      questions: [
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major payment methods through Razorpay including credit cards, debit cards, UPI, and net banking.'
        },
        {
          q: 'Are there any hidden costs?',
          a: 'No hidden costs! The price you see is what you pay. However, you\'ll need to handle your own hosting and any third-party API costs (like payment gateways, email services, etc.) based on your usage.'
        },
        {
          q: 'Can I purchase licenses for multiple projects?',
          a: 'Yes! We offer single-site licenses, developer bundles for multiple projects, and enterprise licenses. Check the pricing section on each product page for details.'
        },
        {
          q: 'Do you offer discounts for bulk purchases?',
          a: 'Yes, we offer volume discounts for agencies and teams. Contact our sales team for custom pricing on multiple licenses or bulk purchases.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen py-16 relative">
      {/* Background decorative elements */}
      <div className="absolute top-40 right-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 left-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      
      <div className="section-container relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-8">
            <HelpCircle size={16} className="text-cyan-400" />
            <span>Got Questions? We've Got Answers</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold font-poppins mb-6">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            Find answers to common questions about our products and services
          </p>
        </div>

        {/* FAQ Categories */}
        <div className="max-w-5xl mx-auto space-y-12">
          {faqs.map((category, catIndex) => (
            <div key={catIndex}>
              <h2 className="text-3xl font-bold mb-6 text-cyan-400 flex items-center space-x-2">
                <span className="w-2 h-8 bg-gradient-to-b from-cyan-400 to-teal-500 rounded-full"></span>
                <span>{category.category}</span>
              </h2>
              <div className="space-y-4">
                {category.questions.map((faq, qIndex) => {
                  const index = `${catIndex}-${qIndex}`;
                  const isOpen = openIndex === index;
                  return (
                    <div key={qIndex} className="glass-card rounded-2xl overflow-hidden border border-cyan-500/10 card-hover">
                      <button
                        onClick={() => toggleFAQ(catIndex, qIndex)}
                        className="w-full px-6 md:px-8 py-5 md:py-6 flex items-center justify-between text-left hover:bg-gray-800/30 transition group"
                      >
                        <span className="font-semibold text-white text-lg pr-4 group-hover:text-cyan-400 transition-colors">{faq.q}</span>
                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center transform transition-all duration-300 ${
                          isOpen ? 'rotate-180 bg-cyan-500/20' : ''
                        }`}>
                          {isOpen ? (
                            <ChevronUp className="text-cyan-400" size={20} />
                          ) : (
                            <ChevronDown className="text-gray-400 group-hover:text-cyan-400 transition-colors" size={20} />
                          )}
                        </div>
                      </button>
                      <div className={`overflow-hidden transition-all duration-300 ${
                        isOpen ? 'max-h-96' : 'max-h-0'
                      }`}>
                        <div className="px-6 md:px-8 pb-6 text-gray-400 leading-relaxed border-t border-gray-800/50">
                          <div className="pt-4">{faq.a}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="max-w-4xl mx-auto mt-20">
          <div className="glass-card p-12 md:p-16 text-center rounded-3xl border-2 border-cyan-500/20 relative overflow-hidden">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl flex items-center justify-center shadow-xl shadow-cyan-500/30">
                <HelpCircle size={36} className="text-gray-900" />
              </div>
              <h2 className="text-4xl font-bold mb-4">Still have questions?</h2>
              <p className="text-gray-400 mb-8 text-lg">
                Can't find the answer you're looking for? Our support team is here to help.
              </p>
              <a href="/contact" className="gradient-button inline-flex items-center justify-center space-x-2 px-10 py-5 rounded-xl shadow-lg shadow-cyan-500/30">
                <span>Contact Support</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;